import { createEventType } from '@remix-run/events';

export type LetterStatus = 'correct' | 'present' | 'absent' | 'empty';

export interface Tile {
  letter: string;
  status: LetterStatus;
}

export type GameStatus = 'playing' | 'won' | 'lost';

// Create custom events
let [change, createChange] = createEventType('wordle:change');
let [gameOver, createGameOver] = createEventType<GameStatus>('wordle:gameover');

// Common 5-letter words for the game
const WORD_LIST = [
  'REACT', 'REMIX', 'ROUTE', 'FRAME', 'BUILD', 'STACK', 'QUERY', 'STATE',
  'PROPS', 'HOOKS', 'CLASS', 'STYLE', 'MOUNT', 'SCOPE', 'ASYNC', 'AWAIT',
  'FETCH', 'CACHE', 'STORE', 'MODEL', 'DEBUG', 'TESTS', 'TYPES', 'INPUT',
  'EVENT', 'CLICK', 'FOCUS', 'HOVER', 'TOUCH', 'SWIPE', 'SLIDE', 'SCALE',
  'LIGHT', 'DARK', 'THEME', 'COLOR', 'PIXEL', 'IMAGE', 'VIDEO', 'AUDIO',
  'TABLE', 'CHART', 'GRAPH', 'ARRAY', 'SLICE', 'SPLIT', 'MERGE', 'JOINT',
];

// Extended word list for validation (includes valid guesses)
const VALID_WORDS = new Set([
  ...WORD_LIST,
  'ABOUT', 'ABOVE', 'ACTOR', 'ACUTE', 'ADMIT', 'ADOPT', 'ADULT', 'AFTER',
  'AGAIN', 'AGENT', 'AGREE', 'AHEAD', 'ALARM', 'ALBUM', 'ALERT', 'ALIGN',
  'ALIVE', 'ALLOW', 'ALONE', 'ALONG', 'ALTER', 'ANGEL', 'ANGER', 'ANGLE',
  'ANGRY', 'APART', 'APPLE', 'APPLY', 'ARENA', 'ARGUE', 'ARISE', 'ARRAY',
  'ARROW', 'ASIDE', 'ASSET', 'AVOID', 'AWARD', 'AWARE', 'BADLY', 'BAKER',
  'BASES', 'BASIC', 'BEACH', 'BEGAN', 'BEGIN', 'BEING', 'BELOW', 'BENCH',
  'BILLY', 'BIRTH', 'BLACK', 'BLADE', 'BLAME', 'BLANK', 'BLIND', 'BLOCK',
  'BLOOD', 'BOARD', 'BOOST', 'BOOTH', 'BOUND', 'BRAIN', 'BRAND', 'BREAD',
  'BREAK', 'BREED', 'BRIEF', 'BRING', 'BROAD', 'BROKE', 'BROWN', 'BUILD',
  'BUILT', 'BUYER', 'CABLE', 'CALIF', 'CARRY', 'CATCH', 'CAUSE', 'CHAIN',
  'CHAIR', 'CHAOS', 'CHARM', 'CHART', 'CHASE', 'CHEAP', 'CHECK', 'CHEST',
  'CHIEF', 'CHILD', 'CHINA', 'CHOSE', 'CIVIC', 'CIVIL', 'CLAIM', 'CLASH',
  'CLASS', 'CLEAN', 'CLEAR', 'CLICK', 'CLOCK', 'CLOSE', 'COACH', 'COAST',
  'COULD', 'COUNT', 'COURT', 'COVER', 'CRACK', 'CRAFT', 'CRASH', 'CRAZY',
  'CREAM', 'CRIME', 'CROSS', 'CROWD', 'CROWN', 'CRUDE', 'CURVE', 'CYCLE',
  'DAILY', 'DANCE', 'DATED', 'DEALT', 'DEATH', 'DEBUT', 'DELAY', 'DEPTH',
  'DOING', 'DOUBT', 'DOZEN', 'DRAFT', 'DRAMA', 'DRANK', 'DRAWN', 'DREAM',
  'DRESS', 'DRILL', 'DRINK', 'DRIVE', 'DROVE', 'DYING', 'EAGER', 'EARLY',
  'EARTH', 'EIGHT', 'ELITE', 'EMPTY', 'ENEMY', 'ENJOY', 'ENTER', 'ENTRY',
  'EQUAL', 'ERROR', 'EVENT', 'EVERY', 'EXACT', 'EXIST', 'EXTRA', 'FAITH',
  'FALSE', 'FAULT', 'FIBER', 'FIELD', 'FIFTH', 'FIFTY', 'FIGHT', 'FINAL',
  'FIRST', 'FIXED', 'FLASH', 'FLEET', 'FLOOR', 'FLUID', 'FOCUS', 'FORCE',
  'FORTH', 'FORTY', 'FORUM', 'FOUND', 'FRAME', 'FRANK', 'FRAUD', 'FRESH',
  'FRONT', 'FRUIT', 'FULLY', 'FUNNY', 'GIANT', 'GIVEN', 'GLASS', 'GLOBE',
  'GOING', 'GRACE', 'GRADE', 'GRAND', 'GRANT', 'GRASS', 'GREAT', 'GREEN',
  'GROSS', 'GROUP', 'GROWN', 'GUARD', 'GUESS', 'GUEST', 'GUIDE', 'HAPPY',
  'HARRY', 'HEART', 'HEAVY', 'HENCE', 'HENRY', 'HORSE', 'HOTEL', 'HOUSE',
  'HUMAN', 'IDEAL', 'IMAGE', 'INDEX', 'INNER', 'INPUT', 'ISSUE', 'JAPAN',
  'JIMMY', 'JOINT', 'JONES', 'JUDGE', 'KNOWN', 'LABEL', 'LARGE', 'LASER',
  'LATER', 'LAUGH', 'LAYER', 'LEARN', 'LEASE', 'LEAST', 'LEAVE', 'LEGAL',
  'LEMON', 'LEVEL', 'LEWIS', 'LIGHT', 'LIMIT', 'LINKS', 'LIVES', 'LOCAL',
  'LOGIC', 'LOOSE', 'LOWER', 'LUCKY', 'LUNCH', 'LYING', 'MAGIC', 'MAJOR',
  'MAKER', 'MARCH', 'MARIA', 'MATCH', 'MAYBE', 'MAYOR', 'MEANT', 'MEDIA',
  'METAL', 'MIGHT', 'MINOR', 'MINUS', 'MIXED', 'MODEL', 'MONEY', 'MONTH',
  'MORAL', 'MOTOR', 'MOUNT', 'MOUSE', 'MOUTH', 'MOVIE', 'MUSIC', 'NEEDS',
  'NEVER', 'NEWLY', 'NIGHT', 'NOISE', 'NORTH', 'NOTED', 'NOVEL', 'NURSE',
  'OCCUR', 'OCEAN', 'OFFER', 'OFTEN', 'ORDER', 'OTHER', 'OUGHT', 'PAINT',
  'PANEL', 'PANIC', 'PAPER', 'PARIS', 'PARTY', 'PEACE', 'PETER', 'PHASE',
  'PHONE', 'PHOTO', 'PIECE', 'PILOT', 'PITCH', 'PLACE', 'PLAIN', 'PLANE',
  'PLANT', 'PLATE', 'POINT', 'POUND', 'POWER', 'PRESS', 'PRICE', 'PRIDE',
  'PRIME', 'PRINT', 'PRIOR', 'PRIZE', 'PROOF', 'PROUD', 'PROVE', 'QUEEN',
  'QUICK', 'QUIET', 'QUITE', 'RADIO', 'RAISE', 'RANGE', 'RAPID', 'RATIO',
  'REACH', 'READY', 'REFER', 'RIGHT', 'RIVAL', 'RIVER', 'ROBIN', 'ROGER',
  'ROMAN', 'ROUGH', 'ROUND', 'ROUTE', 'ROYAL', 'RURAL', 'SCALE', 'SCENE',
  'SCOPE', 'SCORE', 'SENSE', 'SERVE', 'SEVEN', 'SHALL', 'SHAPE', 'SHARE',
  'SHARP', 'SHEET', 'SHELF', 'SHELL', 'SHIFT', 'SHINE', 'SHIRT', 'SHOCK',
  'SHOOT', 'SHORT', 'SHOWN', 'SIGHT', 'SINCE', 'SIXTH', 'SIXTY', 'SIZED',
  'SKILL', 'SLEEP', 'SLIDE', 'SMALL', 'SMART', 'SMILE', 'SMITH', 'SMOKE',
  'SOLID', 'SOLVE', 'SORRY', 'SOUND', 'SOUTH', 'SPACE', 'SPARE', 'SPEAK',
  'SPEED', 'SPEND', 'SPENT', 'SPLIT', 'SPOKE', 'SPORT', 'STAFF', 'STAGE',
  'STAKE', 'STAND', 'START', 'STATE', 'STEAM', 'STEEL', 'STICK', 'STILL',
  'STOCK', 'STONE', 'STOOD', 'STORE', 'STORM', 'STORY', 'STRIP', 'STUCK',
  'STUDY', 'STUFF', 'STYLE', 'SUGAR', 'SUITE', 'SUPER', 'SWEET', 'TABLE',
  'TAKEN', 'TASTE', 'TAXES', 'TEACH', 'TERRY', 'TEXAS', 'THANK', 'THEFT',
  'THEIR', 'THEME', 'THERE', 'THESE', 'THICK', 'THING', 'THINK', 'THIRD',
  'THOSE', 'THREE', 'THREW', 'THROW', 'TIGHT', 'TIMES', 'TITLE', 'TODAY',
  'TOPIC', 'TOTAL', 'TOUCH', 'TOUGH', 'TOWER', 'TRACK', 'TRADE', 'TRAIL',
  'TRAIN', 'TRAIT', 'TREAT', 'TREND', 'TRIAL', 'TRIBE', 'TRICK', 'TRIED',
  'TRIES', 'TROOP', 'TRUCK', 'TRULY', 'TRUNK', 'TRUST', 'TRUTH', 'TWICE',
  'UNDER', 'UNDUE', 'UNION', 'UNITY', 'UNTIL', 'UPPER', 'URBAN', 'USAGE',
  'USUAL', 'VALID', 'VALUE', 'VIDEO', 'VIRUS', 'VISIT', 'VITAL', 'VOCAL',
  'VOICE', 'WASTE', 'WATCH', 'WATER', 'WHEEL', 'WHERE', 'WHICH', 'WHILE',
  'WHITE', 'WHOLE', 'WHOSE', 'WOMAN', 'WOMEN', 'WORLD', 'WORRY', 'WORSE',
  'WORST', 'WORTH', 'WOULD', 'WOUND', 'WRITE', 'WRONG', 'WROTE', 'YIELD',
  'YOUNG', 'YOUTH',
]);

export class WordleGame extends EventTarget {
  static change = change;
  static gameOver = gameOver;

  #answer: string;
  #guesses: Tile[][] = [];
  #currentGuess: string = '';
  #maxGuesses: number = 6;
  #status: GameStatus = 'playing';
  #letterStatuses: Map<string, LetterStatus> = new Map();

  constructor() {
    super();
    this.#answer = this.#selectRandomWord();
  }

  #selectRandomWord(): string {
    return WORD_LIST[Math.floor(Math.random() * WORD_LIST.length)];
  }

  get guesses(): Tile[][] {
    return this.#guesses;
  }

  get currentGuess(): string {
    return this.#currentGuess;
  }

  get currentRow(): number {
    return this.#guesses.length;
  }

  get status(): GameStatus {
    return this.#status;
  }

  get maxGuesses(): number {
    return this.#maxGuesses;
  }

  get answer(): string {
    return this.#status !== 'playing' ? this.#answer : '';
  }

  getLetterStatus(letter: string): LetterStatus {
    return this.#letterStatuses.get(letter) || 'empty';
  }

  addLetter(letter: string): void {
    if (this.#status !== 'playing' || this.#currentGuess.length >= 5) {
      return;
    }

    this.#currentGuess += letter.toUpperCase();
    this.dispatchEvent(createChange());
  }

  removeLetter(): void {
    if (this.#status !== 'playing' || this.#currentGuess.length === 0) {
      return;
    }

    this.#currentGuess = this.#currentGuess.slice(0, -1);
    this.dispatchEvent(createChange());
  }

  submitGuess(): { success: boolean; message?: string } {
    if (this.#status !== 'playing') {
      return { success: false, message: 'Game is over' };
    }

    if (this.#currentGuess.length !== 5) {
      return { success: false, message: 'Not enough letters' };
    }

    if (!VALID_WORDS.has(this.#currentGuess)) {
      return { success: false, message: 'Not in word list' };
    }

    // Evaluate the guess
    const tiles = this.#evaluateGuess(this.#currentGuess);
    this.#guesses.push(tiles);

    // Update letter statuses
    tiles.forEach(tile => {
      const currentStatus = this.#letterStatuses.get(tile.letter);
      // Priority: correct > present > absent
      if (!currentStatus || 
          (tile.status === 'correct') ||
          (tile.status === 'present' && currentStatus !== 'correct')) {
        this.#letterStatuses.set(tile.letter, tile.status);
      }
    });

    // Check win condition
    if (this.#currentGuess === this.#answer) {
      this.#status = 'won';
      this.dispatchEvent(createGameOver({ detail: 'won' }));
    } else if (this.#guesses.length >= this.#maxGuesses) {
      this.#status = 'lost';
      this.dispatchEvent(createGameOver({ detail: 'lost' }));
    }

    this.#currentGuess = '';
    this.dispatchEvent(createChange());

    return { success: true };
  }

  #evaluateGuess(guess: string): Tile[] {
    const tiles: Tile[] = [];
    const answerLetters = this.#answer.split('');
    const guessLetters = guess.split('');

    // First pass: mark correct letters
    const remainingAnswerLetters = [...answerLetters];
    const statuses: (LetterStatus | null)[] = Array(5).fill(null);

    guessLetters.forEach((letter, i) => {
      if (letter === answerLetters[i]) {
        statuses[i] = 'correct';
        remainingAnswerLetters[i] = '';
      }
    });

    // Second pass: mark present letters
    guessLetters.forEach((letter, i) => {
      if (statuses[i] === null) {
        const index = remainingAnswerLetters.indexOf(letter);
        if (index !== -1) {
          statuses[i] = 'present';
          remainingAnswerLetters[index] = '';
        } else {
          statuses[i] = 'absent';
        }
      }
    });

    guessLetters.forEach((letter, i) => {
      tiles.push({
        letter,
        status: statuses[i]!,
      });
    });

    return tiles;
  }

  reset(): void {
    this.#answer = this.#selectRandomWord();
    this.#guesses = [];
    this.#currentGuess = '';
    this.#status = 'playing';
    this.#letterStatuses.clear();
    this.dispatchEvent(createChange());
  }
}

